import { useState, useEffect, useRef } from 'react';
import { Eye, EyeOff, Mail, Lock, ArrowRight, CheckCircle, AlertCircle, Loader2, Sparkles, Shield, Zap, Heart, Star, Phone, PhoneCall } from 'lucide-react';
import { GoogleCredentialResponse, GoogleLogin, GoogleOAuthProvider } from '@react-oauth/google';
import { googleLoginApi, loginApi } from './userapis';
import metafitPinkLogo from '../../assets/MetafitPInk.png';
import { sendOtp, verifyOtp } from '../instrutorProfile/profileAPIs';
import { useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { phoneLoginApi } from './userapis';

const LoginPage = () => {
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [loginStep, setLoginStep] = useState('form'); // 'form', 'loading', 'success', 'error'
    const [loginMethod, setLoginMethod] = useState('phone'); // CHANGED: 'phone' as default instead of 'email'
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        rememberMe: false
    });
    const [phoneNumber, setPhoneNumber] = useState('');

    // OTP State
    const [otp, setOtp] = useState<string[]>(['', '', '', '', '', '']);
    const [otpSent, setOtpSent] = useState(false);
    const [otpVerified, setOtpVerified] = useState(false);
    const [isSendingOTP, setIsSendingOTP] = useState(false);
    const [isVerifyingOTP, setIsVerifyingOTP] = useState(false);
    const [otpError, setOtpError] = useState('');
    const [resendTimer, setResendTimer] = useState(0);
    const otpRefs = useRef<(HTMLInputElement | null)[]>([]);

    const navigate = useNavigate();
    const location = useLocation();
    const [errors, setErrors] = useState({
        email: "",
        password: "",
        phone: ""
    });
    const googleAPI = process.env.REACT_APP_API_GOOGLE_OAUTH;
    const [passwordStrength, setPasswordStrength] = useState(0);
    const [showWelcomeAnimation, setShowWelcomeAnimation] = useState(true);

    useEffect(() => {
        const timer = setTimeout(() => setShowWelcomeAnimation(false), 2000);
        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        const statePhone = (location.state as any)?.phone as string | undefined;
        if (statePhone) {
            setLoginMethod('phone');
            setPhoneNumber(statePhone);
        }
    }, [location.state]);

    // Resend timer effect
    useEffect(() => {
        if (resendTimer > 0) {
            const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [resendTimer]);

    const validateEmail = (email: string) => {
        const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return re.test(email);
    };

    const validatePhone = (phone: string) => {
        return /^\d{10}$/.test(phone.replace(/\D/g, ''));
    };

    const calculatePasswordStrength = (password: string) => {
        let strength = 0;
        if (password.length >= 8) strength += 25;
        if (/[A-Z]/.test(password)) strength += 25;
        if (/[0-9]/.test(password)) strength += 25;
        if (/[^A-Za-z0-9]/.test(password)) strength += 25;
        return strength;
    };

    const handleInputChange = (e: any) => {
        const { name, value, type, checked } = e.target;
        const newValue = type === 'checkbox' ? checked : value;

        setFormData(prev => ({
            ...prev,
            [name]: newValue
        }));

        // Real-time validation
        if (name === 'email') {
            if (value && !validateEmail(value)) {
                setErrors(prev => ({ ...prev, email: 'Please enter a valid email address' }));
            } else {
                setErrors(prev => ({ ...prev, email: '' }));
            }
        }

        if (name === 'password') {
            setPasswordStrength(calculatePasswordStrength(value));
            if (value.length < 6) {
                setErrors(prev => ({ ...prev, password: 'Password must be at least 6 characters' }));
            } else {
                setErrors(prev => ({ ...prev, password: '' }));
            }
        }
    };

    // Handle OTP input changes
    const handleOtpChange = (index: number, value: string) => {
        if (!/^\d?$/.test(value)) return;

        const newOtp = [...otp];
        newOtp[index] = value;
        setOtp(newOtp);

        if (value && index < 5) {
            otpRefs.current[index + 1]?.focus();
        }
    };

    // Handle OTP input keydown events
    const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            otpRefs.current[index - 1]?.focus();
        }
    };

    // Send OTP function
    const handleSendOtp = async () => {
        const phone = phoneNumber.replace(/\D/g, '');

        if (!validatePhone(phone)) {
            toast.error('Please enter a valid 10-digit phone number');
            setErrors(prev => ({ ...prev, phone: 'Please enter a valid 10-digit phone number' }));
            return;
        }

        setIsSendingOTP(true);
        setOtpError('');
        setErrors(prev => ({ ...prev, phone: '' }));

        try {
            const res = await sendOtp(phone);

            if (res.success) {
                toast.success('OTP sent successfully!');
                setOtpSent(true);
                setResendTimer(60);
                setOtp(['', '', '', '', '', '']);
                setTimeout(() => otpRefs.current[0]?.focus(), 100);
            } else {
                toast.error(res.message || 'Failed to send OTP');
            }
        } catch {
            toast.error('Failed to send OTP. Please try again.');
        } finally {
            setIsSendingOTP(false);
        }
    };

    // Verify OTP function
    const handleVerifyOtp = async () => {
        const otpValue = otp.join('');

        if (otpValue.length !== 6) {
            toast.error('Please enter complete 6-digit OTP');
            return;
        }

        setIsVerifyingOTP(true);

        try {
            const phone = phoneNumber.replace(/\D/g, '');

            // First verify OTP with your OTP service
            const otpRes = await verifyOtp(phone, otpValue);

            if (otpRes.success && otpRes.verified) {
                toast.success('Phone number verified successfully!');
                setOtpVerified(true);
                setOtpError('');

                // Now login with verified phone
                await handlePhoneLogin(phone);
            } else {
                setOtpError(otpRes.message || 'Invalid OTP. Please try again.');
                setOtp(['', '', '', '', '', '']);
                otpRefs.current[0]?.focus();
            }
        } catch {
            setOtpError('Verification failed. Please try again.');
        } finally {
            setIsVerifyingOTP(false);
        }
    };

    // Handle phone login after OTP verification
    const handlePhoneLogin = async (phone: string) => {
        setIsLoading(true);
        setLoginStep('loading');

        try {
            // Call phone login API
            const apiRes = await phoneLoginApi(phone);

            if (apiRes && apiRes.success) {
                toast.success('Login successful!');

                // Store token
                sessionStorage.setItem('userAccessToken', apiRes.token || '');

                // Dispatch auth change event
                window.dispatchEvent(new CustomEvent('auth-change'));

                setLoginStep('success');

                // Redirect to profile
                setTimeout(() => {
                    navigate("/profile", { replace: true });
                }, 1500);
            } else {
                setLoginStep('error');
                setErrors(prev => ({ ...prev, phone: apiRes?.message || 'Login failed' }));

                setTimeout(() => {
                    // Reset phone login to show empty input field
                    resetPhoneLogin();
                    setLoginStep('form');
                    setIsLoading(false);
                }, 1500);
            }
        } catch (error) {
            console.error("Phone login error:", error);
            setLoginStep('error');
            setErrors(prev => ({ ...prev, phone: 'Login failed. Please try again.' }));

            setTimeout(() => {
                // Reset phone login to show empty input field
                resetPhoneLogin();
                setLoginStep('form');
                setIsLoading(false);
            }, 1500);
        }
    };

    const handleSubmit = async (e: any) => {
        e.preventDefault();

        if (loginMethod === 'phone') {
            if (!otpVerified) {
                await handleSendOtp();
                return;
            }
            await handlePhoneLogin(phoneNumber.replace(/\D/g, ''));
            return;
        }

        // Email login
        setIsLoading(true);
        setLoginStep('loading');

        const apiRes = await loginApi(formData);
        console.log("Login API Response:", apiRes);

        if (apiRes && apiRes["success"]) {
            const isSuccess = Math.random() > 0.3;

            if (isSuccess) {
                setLoginStep('success');
                if (apiRes.token) {
                    sessionStorage.setItem('userAccessToken', apiRes.token);
                } else {
                    console.warn('loginApi returned no token');
                }
                window.dispatchEvent(new CustomEvent('auth-change'));
                navigate("/profile", { replace: true });
            }
        } else if (apiRes && apiRes["message"]) {
            setLoginStep('error');
            setErrors(prev => ({ ...prev, email: apiRes.message }));
            setTimeout(() => {
                setLoginStep('form');
                setIsLoading(false);
            }, 1000);
        }
    };

    const getPasswordStrengthColor = () => {
        if (passwordStrength < 25) return 'bg-red-500';
        if (passwordStrength < 50) return 'bg-orange-500';
        if (passwordStrength < 75) return 'bg-yellow-500';
        return 'bg-green-500';
    };

    const getPasswordStrengthText = () => {
        if (passwordStrength < 25) return 'Weak';
        if (passwordStrength < 50) return 'Fair';
        if (passwordStrength < 75) return 'Good';
        return 'Strong';
    };

    const handleGoogleLogin = async (response: GoogleCredentialResponse) => {
        if (response.credential) {
            setIsLoading(true);
            setLoginStep('loading');
            const res = await googleLoginApi(response.credential);
            console.log("Google Login API Response:", res);
            if (res) {
                if (res.token) {
                    sessionStorage.setItem('userAccessToken', res.token);
                } else {
                    console.warn('googleLoginApi returned no token');
                }
                console.log("Token:", res);
                window.dispatchEvent(new CustomEvent('auth-change'));
                setLoginStep('success');
                setTimeout(() => {
                    navigate("/profile", { replace: true });
                }, 1500);
            }
        }
    };

    const resetPhoneLogin = () => {
        setOtpSent(false);
        setOtpVerified(false);
        setOtp(['', '', '', '', '', '']);
        setOtpError('');
        setPhoneNumber('');
        setResendTimer(0);
        setErrors(prev => ({ ...prev, phone: '' }));
    };

    const resetEmailLogin = () => {
        setFormData({
            email: '',
            password: '',
            rememberMe: false
        });
        setErrors({ email: '', password: '', phone: '' });
        setPasswordStrength(0);
    };

    return (
        <div className="min-h-screen bg-[#F5F0E3] relative overflow-x-hidden">
            {/* Animated Background Elements */}
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute top-20 left-10 w-20 h-20 bg-[#ECE4CF] rounded-full opacity-50 animate-pulse"></div>
                <div className="absolute top-40 right-20 w-16 h-16 bg-[#3F6652] rounded-full opacity-10 animate-bounce" style={{ animationDelay: '1s' }}></div>
                <div className="absolute bottom-20 left-20 w-24 h-24 bg-[#ECE4CF] rounded-full opacity-50 animate-pulse" style={{ animationDelay: '2s' }}></div>
                <div className="absolute bottom-40 right-10 w-12 h-12 bg-[#3F6652] rounded-full opacity-10 animate-bounce" style={{ animationDelay: '0.5s' }}></div>
            </div>

            {/* Main Content */}
            <div className="grid place-items-center min-h-screen px-4 py-12">
                <div className="max-w-md w-full">
                    {/* Welcome Animation */}
                    {showWelcomeAnimation && (
                        <div className="fixed inset-0 bg-[#15302B] flex items-center justify-center z-50">
                            <div className="text-center text-[#F5F0E3]">
                                <div className="w-16 h-16 bg-[#F5F0E3] rounded-full flex items-center justify-center mx-auto mb-4 animate-bounce">
                                    <Sparkles className="h-8 w-8 text-[#15302B]" />
                                </div>
                                <h2 className="text-2xl font-bold mb-2 animate-pulse">Welcome Back!</h2>
                                <p className="text-[#E6A93F]">Continue your wellness journey...</p>
                            </div>
                        </div>
                    )}

                    {/* Wellness Call-to-Action Banner */}
                    {loginStep === 'form' && (
                        <div className={`mb-4 bg-[#15302B] rounded-2xl p-4 text-center shadow-2xl transform transition-all duration-700 ${!showWelcomeAnimation ? 'scale-100 opacity-100 translate-y-0' : 'scale-95 opacity-0 translate-y-4'}`}>
                            <div className="flex items-center justify-center mb-2">
                                <div className="flex space-x-2">
                                    <Star className="h-5 w-5 text-[#E6A93F] animate-pulse" />
                                    <Heart className="h-5 w-5 text-[#E6A93F] animate-bounce" />
                                    <Sparkles className="h-5 w-5 text-[#E6A93F] animate-pulse" style={{ animationDelay: '0.5s' }} />
                                </div>
                            </div>
                            <h3 className="text-[#F5F0E3] font-bold text-base mb-1">
                                Stop waiting—your wellness journey starts NOW!
                            </h3>
                            <p className="text-[#ECE4CF] text-xs font-medium">
                                Meta Fit Wellness: Yoga, Ayurveda & more at your fingertips!
                            </p>
                            <div className="mt-2 flex justify-center">
                                <div className="w-12 h-1 bg-[#F5F0E3]/30 rounded-full">
                                    <div className="w-full h-full bg-[#E6A93F] rounded-full animate-pulse"></div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Login Card */}
                    <div className={`bg-white/90 backdrop-blur-lg rounded-2xl shadow-2xl p-6 border border-white/20 transform transition-all duration-500 ${!showWelcomeAnimation ? 'scale-100 opacity-100' : 'scale-95 opacity-0'}`}>

                        {loginStep === 'form' && (
                            <>
                                <div className="text-center mb-4">
                                    <div className="w-16 h-16 mx-auto mb-2 transform hover:scale-105 transition-transform duration-300 flex items-center justify-center">
                                        <img src={metafitPinkLogo} alt="Metafit Wellness" className="h-full w-auto object-contain" />
                                    </div>
                                    <h1 className="text-2xl font-bold text-[#15302B] mb-1 animate-fade-in">Welcome Back</h1>
                                    <p className="text-sm text-gray-600 animate-fade-in" style={{ animationDelay: '0.2s' }}>Continue your wellness journey with us</p>
                                </div>

                                <div className="space-y-4">
                                        <>
                                            {/* Phone Input */}
                                            <div className="transform transition-all duration-300 hover:scale-[1.01]">
                                                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                                                    Phone Number
                                                </label>
                                                <div className="relative">
                                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                        <Phone className={`h-5 w-5 transition-colors duration-200 ${errors.phone ? 'text-red-400' : phoneNumber ? 'text-[#15302B]' : 'text-gray-400'}`} />
                                                    </div>
                                                    <input
                                                        id="phone"
                                                        name="phone"
                                                        type="tel"
                                                        autoComplete="tel"
                                                        value={phoneNumber}
                                                        onChange={(e) => {
                                                            const value = e.target.value.replace(/\D/g, '');
                                                            setPhoneNumber(value);
                                                            if (otpSent) {
                                                                resetPhoneLogin();
                                                            }
                                                        }}
                                                        className={`appearance-none block w-full pl-10 pr-3 py-2 border rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#15302B] focus:border-transparent transition-all duration-200 ${errors.phone ? 'border-red-300 bg-red-50' : 'border-gray-300 hover:border-[#3F6652]'}`}
                                                        placeholder="Enter your 10-digit phone number"
                                                        maxLength={10}
                                                        disabled={otpSent}
                                                    />
                                                </div>
                                                {errors.phone && (
                                                    <p className="mt-1 text-sm text-red-600 flex items-center animate-slide-down">
                                                        <AlertCircle className="h-4 w-4 mr-1" />
                                                        {errors.phone}
                                                    </p>
                                                )}
                                            </div>

                                            {/* OTP Section */}
                                            {otpSent && !otpVerified && (
                                                <div className="space-y-4 animate-slide-down">
                                                    <div className="text-center">
                                                        <p className="text-sm text-gray-600 mb-3">
                                                            Enter the 6-digit OTP sent to {phoneNumber}
                                                        </p>
                                                        <div className="flex justify-center gap-1.5 sm:gap-2 mb-4">
                                                            {otp.map((digit, index) => (
                                                                <input
                                                                    key={index}
                                                                    ref={(el) => (otpRefs.current[index] = el)}
                                                                    maxLength={1}
                                                                    value={digit}
                                                                    onChange={(e) => handleOtpChange(index, e.target.value)}
                                                                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                                                                    className="w-9 h-10 sm:w-12 sm:h-12 text-center border border-gray-300 rounded-lg text-base sm:text-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all hover:border-teal-300"
                                                                    type="text"
                                                                    inputMode="numeric"
                                                                    disabled={isVerifyingOTP}
                                                                />
                                                            ))}
                                                        </div>

                                                        {otpError && (
                                                            <p className="mt-2 text-center text-red-500 text-sm">{otpError}</p>
                                                        )}

                                                        <div className="flex gap-2">
                                                            <button
                                                                type="button"
                                                                onClick={handleVerifyOtp}
                                                                disabled={isVerifyingOTP || otp.join('').length !== 6}
                                                                className={`flex-1 py-2 rounded-lg transition-all ${isVerifyingOTP || otp.join('').length !== 6 ? 'bg-gray-400 cursor-not-allowed' : 'bg-[#E6A93F] hover:bg-[#C98726] text-[#15302B]'} font-semibold`}
                                                            >
                                                                {isVerifyingOTP ? (
                                                                    <span className="flex items-center justify-center gap-2">
                                                                        <Loader2 className="h-4 w-4 animate-spin" />
                                                                        Verifying...
                                                                    </span>
                                                                ) : 'Verify OTP'}
                                                            </button>

                                                            <button
                                                                type="button"
                                                                onClick={resetPhoneLogin}
                                                                className="px-4 py-3 text-teal-600 hover:text-teal-700 text-sm font-medium transition-colors"
                                                            >
                                                                Change Number
                                                            </button>
                                                        </div>

                                                        {resendTimer > 0 ? (
                                                            <p className="text-center text-sm text-gray-500 mt-2">
                                                                Resend OTP in {resendTimer}s
                                                            </p>
                                                        ) : (
                                                            <button
                                                                type="button"
                                                                onClick={handleSendOtp}
                                                                className="text-teal-600 hover:text-teal-700 text-sm mx-auto block mt-2 transition-colors"
                                                            >
                                                                Resend OTP
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Send OTP Button */}
                                            {!otpSent && (
                                                <button
                                                    type="button"
                                                    onClick={handleSendOtp}
                                                    disabled={isSendingOTP || !validatePhone(phoneNumber)}
                                                    className="group relative w-full flex justify-center py-2.5 px-4 border border-transparent text-sm font-semibold rounded-lg text-[#15302B] bg-[#E6A93F] hover:bg-[#C98726] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#15302B] transition-all duration-200 transform hover:scale-[1.01] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none shadow-md hover:shadow-lg"
                                                >
                                                    {isSendingOTP ? (
                                                        <span className="flex items-center justify-center gap-2">
                                                            <PhoneCall className="h-4 w-4" />
                                                            Sending OTP...
                                                        </span>
                                                    ) : 'Send OTP to Login'}
                                                </button>
                                            )}

                                            {/* Verified Status */}
                                            {otpVerified && (
                                                <div className="text-center py-4">
                                                    <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-4 py-2 rounded-full">
                                                        <CheckCircle className="h-4 w-4" />
                                                        Phone Verified
                                                    </div>
                                                    <p className="text-sm text-gray-600 mt-2">
                                                        Click the login button below to continue
                                                    </p>
                                                </div>
                                            )}
                                        </>

                                </div>
                            </>
                        )}

                        {/* Loading State */}
                        {loginStep === 'loading' && (
                            <div className="text-center py-12 animate-fade-in">
                                <div className="w-16 h-16 bg-[#15302B] rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
                                    <Loader2 className="h-8 w-8 text-[#E6A93F] animate-spin" />
                                </div>
                                <h3 className="text-xl font-semibold text-gray-900 mb-2">Signing you in...</h3>
                                <p className="text-gray-600">Please wait while we verify your credentials</p>
                            </div>
                        )}

                        {/* Success State */}
                        {loginStep === 'success' && (
                            <div className="text-center py-12 animate-fade-in">
                                <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4 animate-bounce">
                                    <CheckCircle className="h-8 w-8 text-white" />
                                </div>
                                <h3 className="text-xl font-semibold text-gray-900 mb-2">Welcome back!</h3>
                                <p className="text-gray-600">Redirecting to your dashboard...</p>
                            </div>
                        )}

                        {/* Error State */}
                        {loginStep === 'error' && (
                            <div className="text-center py-12 animate-fade-in">
                                <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
                                    <AlertCircle className="h-8 w-8 text-white" />
                                </div>
                                <h3 className="text-xl font-semibold text-gray-900 mb-2">Login Failed</h3>
                                <p className="text-gray-600 mb-4">Invalid credentials. Please try again.</p>
                            </div>
                        )}

                        {/* Social Login - Only show in form state */}
                        {loginStep === 'form' && (
                            <div className="mt-4 animate-fade-in" style={{ animationDelay: '0.4s' }}>
                                <div className="relative my-3">
                                    <div className="absolute inset-0 flex items-center">
                                        <div className="w-full border-t border-gray-200" />
                                    </div>
                                    <div className="relative flex justify-center text-xs">
                                        <span className="px-2 bg-white text-gray-500">Or continue with</span>
                                    </div>
                                </div>

                                <div className="w-full flex justify-center hover:scale-[1.02] transition-transform duration-300">
                                    <GoogleOAuthProvider clientId={googleAPI || ""}>
                                        <GoogleLogin
                                            onSuccess={handleGoogleLogin}
                                            onError={() => window.location.reload}
                                            width={280}
                                        />
                                    </GoogleOAuthProvider>
                                </div>
                            </div>
                        )}

                        {/* Sign Up Link - Only show in form state */}
                        {loginStep === 'form' && (
                            <div className="text-center mt-6 animate-fade-in" style={{ animationDelay: '0.6s' }}>
                                <p className="text-sm text-gray-600">
                                    Don't have an account?{' '}
                                    <a href="/signup" className="font-medium text-teal-600 hover:text-teal-500 transform hover:scale-105 transition-all duration-200 inline-block">
                                        Sign up for free
                                    </a>
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Security Features */}
                    {loginStep === 'form' && (
                        <div className="mt-6 bg-white/70 backdrop-blur-sm rounded-xl p-4 border border-white/20 animate-fade-in" style={{ animationDelay: '0.8s' }}>
                            <div className="flex items-center justify-center space-x-6">
                                <div className="flex items-center text-sm text-gray-600">
                                    <Shield className="h-4 w-4 text-teal-600 mr-2" />
                                    SSL Encrypted
                                </div>
                                <div className="flex items-center text-sm text-gray-600">
                                    <Zap className="h-4 w-4 text-teal-600 mr-2" />
                                    Fast & Secure
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Additional Info */}
                    <div className="text-center mt-6 animate-fade-in" style={{ animationDelay: '1s' }}>
                        <p className="text-xs text-gray-500">
                            By signing in, you agree to our{' '}
                            <a href="#" className="text-teal-600 hover:text-teal-500 transition-colors duration-200">Terms of Service</a>{' '}
                            and{' '}
                            <a href="#" className="text-teal-600 hover:text-teal-500 transition-colors duration-200">Privacy Policy</a>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LoginPage;
